import {
  shopOrderForStoreDataAtomFamily,
  shopOrderForStoreStatusAtomFamily,
  shopOrderListAgencyAdminDataAtom,
  shopOrderListAgencyAdminStatusAtom,
  shopOrderListForAgencyAdminDataAtom,
  shopOrderListForAgencyAdminStatusAtom,
  shopOrderListForSellerByShopAccountDataAtomFamily,
  shopOrderListForSellerByShopAccountStatusAtomFamily,
  shopOrderListForSellerByShopNeedAccountDataAtomFamily,
  shopOrderListForSellerByShopNeedAccountStatusAtomFamily,
  shopOrderListForSellerDataAtomFamily,
  shopOrderListForSellerStatusAtomFamily,
  shopOrderListForWorkerDataAtom,
  shopOrderListForWorkerStatusAtom,
  shopOrderNotPaidListForSellerByShopDataAtomFamily,
  shopOrderNotPaidListForSellerByShopStatusAtomFamily,
  shopOrderWaitingListForSellerByShopDataAtomFamily,
  shopOrderWaitingListForSellerByShopStatusAtomFamily,
} from '@shared/api/shopOrder/shopOrder.atom';
import {
  ShopOrderCashReceiptStatus,
  ShopOrderDocStatus,
  StoreRole,
} from '@shared/api/shopOrder/shopOrder.interface';
import { fetcher, queryString, useRecoilQuery } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { DeliveryState, LowerRole, PaymentState } from '@shared/types';
import _ from 'lodash';

import {
  shopOrderListForStoreDataAtom,
  shopOrderListForStoreStatusAtom,
} from './shopOrder.atom';

const shopOrderForStorePopulate = {
  populate: [
    {
      path: 'shop',
    },
    {
      path: 'orderDetail.shopItem',
      populate: [
        {
          path: 'shopItemInfo',
          populate: {
            path: 'image',
          },
        },
        {
          path: 'image',
        },
      ],
    },
    {
      path: 'deliveryDetail.deliveredImage',
    },
  ],
};

// admin, bugo-agency-admin, bugo-agency, store-customer, public(default)

const vaildRolesShopOrderForStore = [
  LowerRole.Admin,
  LowerRole.StoreCustomer,
  LowerRole.StoreGuest,
];

export const shopOrderListForStoreUrl = (storeRole: StoreRole) =>
  `/${storeRole}/shop-order/?${queryString(shopOrderForStorePopulate)}`;

export const useShopOrderListForStoreHook = (storeRole: StoreRole) => {
  const disabled = !_.includes(vaildRolesShopOrderForStore, storeRole);

  useRecoilQuery(
    shopOrderListForStoreStatusAtom,
    shopOrderListForStoreDataAtom,
    shopOrderListForStoreUrl(storeRole),
    fetcher,
    disabled,
  );
};

export const shopOrderForStoreUrl = (shopOrderId: string, storeRole: StoreRole) => {
  return `/${storeRole}/shop-order/${shopOrderId}?${queryString(
    shopOrderForStorePopulate,
  )}`;
};

export const useShopOrderForStoreHook = (
  shopOrderId: string,
  storeRole: StoreRole,
  disableState: boolean,
) => {
  const disabled = !(_.includes(vaildRolesShopOrderForStore, storeRole) || disableState);

  useRecoilQuery(
    shopOrderForStoreStatusAtomFamily(shopOrderId),
    shopOrderForStoreDataAtomFamily(shopOrderId),
    shopOrderForStoreUrl(shopOrderId, storeRole),
    fetcher,
    disabled,
  );
};

export const usePublicShopOrderForStoreHook = (
  shopOrderId: string,
  storeRole: StoreRole,
  disableState: boolean,
) => {
  const disabled = !(_.includes(vaildRolesShopOrderForStore, storeRole) || disableState);

  useRecoilQuery(
    shopOrderForStoreStatusAtomFamily(shopOrderId),
    shopOrderForStoreDataAtomFamily(shopOrderId),
    shopOrderForStoreUrl(shopOrderId, LowerRole.Public),
    fetcher,
    disabled,
  );
};

//*상조관리자가 볼 화환내역 페이지용
export const shopOrderListByBugoBrandUrl = (bugoRole: LowerRole) => {
  return `/${bugoRole}/shop-order/?${queryString({
    filter: {
      'paymentDetail.status': { $in: [PaymentState.PAID, PaymentState.CONFIRMED] },
    },
    populate: [
      {
        path: 'fevent',
        populate: {
          path: 'user',
        },
      },
      {
        path: 'deliveryDetail.deliveredImage',
      },
      {
        path: 'funeralHomeInfo',
      },
    ],
    options: {
      sort: { createdAt: -1 },
    },
  })}`;
};

//*상조관리자가 볼 화환내역 페이지용
export const useShopOrderListByBugoBrandHook = () => {
  const { bugoRole } = useAuth();

  useRecoilQuery(
    shopOrderListForAgencyAdminStatusAtom,
    shopOrderListForAgencyAdminDataAtom,
    shopOrderListByBugoBrandUrl(bugoRole()),
    fetcher,
  );
};

//*상조관리자가 볼 화환내역 페이지용 (refactoring)
export const shopOrderListAgencyAdminHookUrl = (
  bugoRole: LowerRole,
  selectYear?: number,
) => {
  const now = new Date();
  const startofYear = new Date(now.getFullYear(), -1, -7);

  if (selectYear) {
    const startofYear = new Date(selectYear, 0, -7);
    const lastofYear = new Date(selectYear + 1, 1, 1);
    return `/${bugoRole}/shop-order/?${queryString({
      select: [
        '_id',
        'paymentDetail',
        'orderDetail',
        'createdAt',
        'fevent',
        'bugoDetail',
        'funeralHomeInfo',
        'deliveryDetail',
      ],
      filter: {
        'paymentDetail.status': { $in: [PaymentState.PAID, PaymentState.CONFIRMED] },
        createdAt: { $gte: startofYear, $lte: lastofYear },
      },
      populate: [
        {
          path: 'fevent',
          select: ['_id', 'user', 'deceasedInfo.name', 'deceasedInfo.coffinOut'],
          populate: {
            path: 'user',
            select: ['_id', 'info', 'bugoAgencyWorkerDetail'],
          },
        },
        { path: 'funeralHomeInfo', select: ['_id', 'name', 'address'] },
        { path: 'deliveryDetail.deliveredImage' },
      ],
      options: {
        sort: { createdAt: -1 },
      },
    })}`;
  } else {
    return `/${bugoRole}/shop-order/?${queryString({
      select: [
        '_id',
        'paymentDetail',
        'orderDetail',
        'createdAt',
        'fevent',
        'bugoDetail',
        'funeralHomeInfo',
        'deliveryDetail',
      ],
      filter: {
        'paymentDetail.status': { $in: [PaymentState.PAID, PaymentState.CONFIRMED] },
        createdAt: { $gte: startofYear },
      },
      populate: [
        {
          path: 'fevent',
          select: ['_id', 'user', 'deceasedInfo.name', 'deceasedInfo.coffinOut'],
          populate: {
            path: 'user',
            select: ['_id', 'info', 'bugoAgencyWorkerDetail'],
          },
        },
        { path: 'funeralHomeInfo', select: ['_id', 'name', 'address'] },
        { path: 'deliveryDetail.deliveredImage' },
      ],
      options: {
        sort: { createdAt: -1 },
      },
    })}`;
  }
};

//*상조관리자가 볼 화환내역 페이지용 (refactoring)
export const useShopOrderListAgencyAdminHookHook = (selectYear?: number) => {
  const { bugoRole } = useAuth();

  useRecoilQuery(
    shopOrderListAgencyAdminStatusAtom,
    shopOrderListAgencyAdminDataAtom,
    shopOrderListAgencyAdminHookUrl(bugoRole(), selectYear),
    fetcher,
  );
};

//* seller용 검색
export const searchShopOrderListForSellerByShopUrl = function (
  shopId: string,
  role: LowerRole,
  searchText: string,
) {
  return `${role}/shop-order/?${queryString({
    populate: [
      {
        path: 'user',
      },
      {
        path: 'bugo',
        populate: [
          {
            path: 'bugoBrand',
          },
          {
            path: 'fevent',
            //* 배송지 주소 수정 모달 — 부고(fevent) 기준 현재 상가 주소 표시용
            populate: [
              {
                path: 'funeralHomeInfo',
                select: ['name', 'address'],
              },
              {
                path: 'roomInfo',
                select: ['name'],
              },
            ],
          },
        ],
      },
      {
        path: 'orderDetail.shopItem',
        populate: [
          {
            path: 'shopItemInfo',
            populate: {
              path: 'image',
            },
          },
          {
            path: 'image',
          },
        ],
      },
      {
        path: 'deliveryDetail',
        populate: {
          path: 'deliveredImage',
        },
      },
    ],
    filter: {
      $and: [
        {
          $or: [
            // If paymentDetail.status is CONFIRMED or PAID, deliveryDetail.status must be DELIVERED
            {
              'paymentDetail.status': {
                $in: [PaymentState.CONFIRMED, PaymentState.PAID],
              },
              'deliveryDetail.status': DeliveryState.DELIVERED,
            },
            // If paymentDetail.status is CANCELED or CANCEL_REQUESTED, deliveryDetail.status can be anything
            {
              'paymentDetail.status': {
                $in: [PaymentState.CANCELED, PaymentState.CANCEL_REQUESTED],
              },
            },
          ],
        },
        {
          $or: [
            { 'orderDetail.shopItemEmbed.name': { $regex: searchText } },
            { 'deliveryDetail.receiverAddress': { $regex: searchText } },
            { 'deliveryDetail.senderName': { $regex: searchText } },
            { 'deliveryDetail.senderPhoneNumber': { $regex: searchText } },
            { 'deliveryDetail.receiverPhoneNumber': { $regex: searchText } },
            { 'deliveryDetail.receiverName': { $regex: searchText } },
            { 'bugoDetail.senderPhrase': { $regex: searchText } },
          ],
        },
      ],
    },
    options: {
      sort: {
        'paymentDetail.purchasedAt': -1,
      },
      limit: 100,
    },
  })}`;
};

export const shopOrderListForSellerUrl = function (role: LowerRole) {
  const now = new Date();

  // MongoDB 8.0 호환성을 위해 날짜를 ISO 문자열로 변환
  const oneDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const fourDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4);

  return `${role}/shop-order/?${queryString({
    select: [
      '_id',
      'user',
      //* shop(ObjectId 스칼라): admin 목록은 여러 shop 주문이 섞여 오므로 상품교체 모달이
      //* 주문별 자기 shop을 알아야 함(route shopId로 대체 불가). populate 아님 — 프로젝션 비용만.
      'shop',
      'bugo',
      'bugoDetail',
      'deliveryDetail',
      'orderDetail',
      'paymentDetail',
      //* 면세계산서/수기환불 요청 상태 배지용 — 구서버(필드 없음)에선 그냥 빠져서 무해
      'taxInvoiceRequest',
      'manualRefund',
      //* 우리 홈택스 자진발행 도장 — 계산서요청 노출 판정이 결제수단보다 이걸 먼저 본다
      //* (가상계좌·카드 주문이라도 상품교체 차액은 우리가 발행한 몫이다)
      'selfCashReceiptDetail',
    ],
    populate: [
      {
        path: 'user',
        //* roles: 게스트(StoreGuest) 주문 판별용 — 구매자 정보수정 버튼 노출 조건
        select: ['info.name', 'info.phoneNumber', 'roles'],
      },
      {
        path: 'bugo',
        select: ['bugoBrand', 'fevent'],
        populate: [
          {
            path: 'bugoBrand',
            select: ['bugoAgency.nickName'],
          },
          {
            path: 'fevent',
            select: [
              'deceasedInfo.name',
              'deceasedInfo.enterance',
              'deceasedInfo.coffinOut',
              //* 배송지 주소 수정 모달 — 부고(fevent) 기준 현재 상가 주소 표시용
              'funeralHomeInfo',
              'funeralHomeInfoEmbed',
              'roomInfo',
              'roomInfoEmbed',
            ],
            populate: [
              {
                path: 'funeralHomeInfo',
                select: ['name', 'address'],
              },
              {
                path: 'roomInfo',
                select: ['name'],
              },
            ],
          },
        ],
      },
      {
        path: 'orderDetail.shopItem',
        select: ['shopItemInfo', 'image', 'priceWhole'],
        populate: [
          {
            path: 'shopItemInfo',
            populate: {
              path: 'image',
            },
          },
          {
            path: 'image',
          },
        ],
      },
      {
        path: 'deliveryDetail',
        populate: {
          path: 'deliveredImage',
        },
      },
    ],
    filter: {
      'paymentDetail.status': {
        $in: [
          PaymentState.CANCELED,
          PaymentState.CONFIRMED,
          PaymentState.PAID,
          PaymentState.CANCEL_REQUESTED,
        ],
      },
      $or: [
        {
          $and: [
            {
              'deliveryDetail.deliveredAt': {
                $gt: oneDayAgo.toISOString(),
              },
            },
            {
              'deliveryDetail.status': DeliveryState.DELIVERED,
            },
          ],
        },
        {
          $and: [
            {
              updatedAt: {
                $gt: fourDaysAgo.toISOString(),
              },
            },
            {
              'deliveryDetail.status': { $ne: DeliveryState.DELIVERED },
            },
          ],
        },
      ],
    },
    options: {
      sort: {
        'paymentDetail.purchasedAt': -1,
      },
    },
  })}`;
};

export const useShopOrderListForSellerHook = function (shopId: string) {
  const { sellerRole } = useAuth();

  useRecoilQuery(
    shopOrderListForSellerStatusAtomFamily(shopId),
    shopOrderListForSellerDataAtomFamily(shopId),
    shopOrderListForSellerUrl(sellerRole()),
    fetcher,
  );
};

//* 면세계산서/수기환불 요청이 존재하는 주문 목록 (요청 관리 페이지용).
//* "요청 전체"는 $exists 대신 status $in 3개 — 백엔드가 걸어둔 status 인덱스를 탄다.
//* seller find는 owningFilter(자기 shop 주문만)가 서버에서 자동 합쳐지므로 스코프 불요.
//* 구서버엔 필드가 없어 빈 목록이거나 populate 단계에서 실패할 수 있음 — 호출부에서 안내 처리.
//* (스펙: docs/shop-order-seller-request-spec.md)
//* 사이드바 대기 건수 배지용 초경량 조회 — _id만 select, populate 없음, status 인덱스 사용.
//* 대기 건수가 월 몇 건 수준이라 사실상 count 비용. 구서버(필드 없음)면 빈 배열.
export const shopOrderSellerRequestPendingCountUrl = function (
  kind: 'taxInvoiceRequest' | 'manualRefund',
  role: LowerRole,
) {
  return `${role}/shop-order/?${queryString({
    select: ['_id'],
    filter: { [`${kind}.status`]: 'Requested' },
  })}`;
};

//* 요청 목록 — 대기/완료 분리 조회. 대기(Requested)는 전량, 완료(Done|Rejected)는
//* 무한히 쌓이므로 processedFrom(기본 최근 1주, 호출부 기간 필터) 이후만. status 필터가 인덱스를 탄다.
export const shopOrderSellerRequestListUrl = function (
  kind: 'taxInvoiceRequest' | 'manualRefund',
  role: LowerRole,
  scope: 'pending' | 'processed' = 'pending',
  processedFrom?: Date,
) {
  const filter =
    scope === 'pending'
      ? { [`${kind}.status`]: 'Requested' }
      : {
          [`${kind}.status`]: { $in: ['Done', 'Rejected'] },
          ...(processedFrom
            ? { [`${kind}.processedAt`]: { $gt: processedFrom.toISOString() } }
            : {}),
        };
  return `${role}/shop-order/?${queryString({
    populate: [
      { path: `${kind}.requestedBy`, select: ['info.name'] },
      { path: `${kind}.processedBy`, select: ['info.name'] },
      ...(kind === 'taxInvoiceRequest'
        ? [{ path: 'taxInvoiceRequest.bizRegImage' }]
        : []),
      ...(role === LowerRole.Admin ? [{ path: 'shop', select: ['name'] }] : []),
    ],
    filter,
    options: {
      sort: {
        [scope === 'pending' ? `${kind}.requestedAt` : `${kind}.processedAt`]: -1,
      },
      limit: 500,
    },
  })}`;
};

//* 현금영수증 도장이 찍힌 주문 목록 (admin 전용) — 신규 API 없이 기존 find + select.
//* 발행 대기 큐(cash-receipt/pending)의 뒷면이다: 잘못 찍은 도장을 되돌리거나,
//* 취소된 주문에 취소 계열 도장을 이어 찍는 화면이 이 목록을 쓴다.
//*
//*! 구서버(스키마에 selfCashReceiptDetail 없음)에선 strictQuery가 이 필터를 조용히 제거해
//*! **전 주문이 매칭된다.** 호출부에서 반드시 row.selfCashReceiptDetail?.status 유무로
//*! 한 번 더 거를 것 (populate가 먼저 StrictPopulateError로 끊길 수도 있다).
export const shopOrderCashReceiptStampedUrl = function (
  statuses: ShopOrderCashReceiptStatus[],
  range?: { from?: Date; to?: Date },
) {
  return `${LowerRole.Admin}/shop-order/?${queryString({
    select: [
      'selfCashReceiptDetail',
      'deliveryDetail.senderName',
      'deliveryDetail.senderPhoneNumber',
      'orderDetail.shopItemEmbed.name',
      'orderDetail.quantity',
      //* 발행액 파생용 — PG 결제 건은 결제총액이 아니라 교체 차액만 우리가 발행했다
      'orderDetail.itemChanges',
      'paymentDetail.status',
      'paymentDetail.method',
      'paymentDetail.purchasedAt',
      'paymentDetail.paymentByCash',
      'paymentDetail.paymentByPoint',
      'paymentDetail.canceledAt',
      'paymentDetail.cancelReason',
    ],
    populate: [{ path: 'selfCashReceiptDetail.statusBy', select: ['info.name'] }],
    filter: {
      'selfCashReceiptDetail.status': { $in: statuses },
      ...(range?.from || range?.to
        ? {
            'paymentDetail.purchasedAt': {
              ...(range.from ? { $gte: range.from.toISOString() } : {}),
              ...(range.to ? { $lte: range.to.toISOString() } : {}),
            },
          }
        : {}),
    },
    options: {
      //* 최근에 찍은 도장이 위로 — 방금 잘못 누른 건을 바로 찾아 되돌릴 수 있어야 한다
      sort: { 'selfCashReceiptDetail.statusAt': -1 },
      limit: 500,
    },
  })}`;
};

export const shopOrderWaitingListForSellerByShopUrl = function (
  shopId: string,
  role: LowerRole,
) {
  return `${role}/shop-order/?${queryString({
    populate: [
      {
        path: 'user',
      },
      {
        path: 'bugo',
        populate: [
          {
            path: 'bugoBrand',
          },
          {
            path: 'fevent',
          },
        ],
      },
      {
        path: 'orderDetail.shopItem',
        populate: [
          {
            path: 'shopItemInfo',
            populate: {
              path: 'image',
            },
          },
          {
            path: 'image',
          },
        ],
      },
    ],
    filter: {
      'paymentDetail.status': {
        $in: [PaymentState.WAITING],
      },
      'paymentDetail.isAccounted': { $ne: true },
      //* 삭제 주문 제외. seller 조회는 서버가 이미 걸러내지만 admin 조회는 안 거른다.
      //* 레거시 주문(필드 없음)도 남아야 하므로 $ne (Active 일치 조건 금지)
      docStatus: { $ne: ShopOrderDocStatus.Deleted },
    },
    options: {
      sort: {
        createdAt: -1,
      },
    },
  })}`;
};

export const shopOrderNotPaidListForSellerByShopUrl = function (role: LowerRole) {
  const now = new Date();
  // wantNow를 현재 날짜의 자정으로 설정

  const threeHoursAgo = new Date(now);
  threeHoursAgo.setHours(now.getHours() - 6);
  threeHoursAgo.setMinutes(0);
  threeHoursAgo.setSeconds(0);
  threeHoursAgo.setMilliseconds(0);

  return `${role}/shop-order/?${queryString({
    populate: [
      {
        path: 'user',
      },
      {
        path: 'bugo',
        populate: [
          {
            path: 'bugoBrand',
          },
          {
            path: 'fevent',
          },
        ],
      },
      {
        path: 'orderDetail.shopItem',
      },
    ],
    filter: {
      'paymentDetail.status': PaymentState.NOT_PAID,
      'paymentDetail.vBank': { $exists: true, $ne: null },
      'paymentDetail.requestAt': { $gte: threeHoursAgo },
      //* 삭제 주문 제외 (admin 조회는 서버가 안 걸러냄). 레거시 주문 보존 위해 $ne
      docStatus: { $ne: ShopOrderDocStatus.Deleted },
    },
    options: {
      sort: {
        createdAt: -1,
      },
    },
  })}`;
};

export const useShopOrderWaitingListForSellerByShopHook = function (shopId: string) {
  const { sellerRole } = useAuth();

  useRecoilQuery(
    shopOrderWaitingListForSellerByShopStatusAtomFamily(shopId),
    shopOrderWaitingListForSellerByShopDataAtomFamily(shopId),
    shopOrderWaitingListForSellerByShopUrl(shopId, sellerRole()),
    fetcher,
  );
};

export const useShopOrderNotPaidListForSellerByShopHook = function (shopId: string) {
  const { sellerRole } = useAuth();
  useRecoilQuery(
    shopOrderNotPaidListForSellerByShopStatusAtomFamily(shopId),
    shopOrderNotPaidListForSellerByShopDataAtomFamily(shopId),
    shopOrderNotPaidListForSellerByShopUrl(sellerRole()),
    fetcher,
  );
};

//* 정산관련 페이지에 들어갈 shopOrder, 정산유무 상관없이 다들고옴 단 최대한 populate 없도록
export const shopOrderListForSellerByShopAccountUrl = function (
  shopId: string,
  role: LowerRole,
  selectYear?: number,
) {
  const now = new Date();
  const startofYear = new Date(now.getFullYear(), 0, -7);

  if (selectYear) {
    const startofYear = new Date(selectYear, 0, -7);
    const lastofYear = new Date(selectYear + 1, 1, 1);
    return `${role}/shop-order/?${queryString({
      populate: [
        {
          path: 'bugo',
          select: ['user', 'bugoBrand', '_id'],
          populate: [
            {
              path: 'user',
              select: ['_id', 'info'],
            },
            {
              path: 'bugoBrand',
              select: ['_id', 'bugoAgency.nickName', 'bugoAgency.corpName'],
            },
          ],
        },
        {
          path: 'fevent',
          select: ['deceasedInfo.coffinOut'],
        },
        {
          path: 'funeralHomeInfo',
        },
      ],
      select: [
        'bugoDetail',
        'deliveryDetail.receiverAddress',
        'paymentDetail',
        'orderDetail',
        'bugoBrand',
        'shop',
      ],
      filter: {
        'paymentDetail.status': {
          $in: [PaymentState.CONFIRMED, PaymentState.PAID],
        },
        createdAt: { $gte: startofYear, $lte: lastofYear },
      },
      options: {
        sort: {},
      },
    })}`;
  } else {
    return `${role}/shop-order/?${queryString({
      populate: [
        {
          path: 'bugo',
          select: ['user', 'bugoBrand', '_id'],
          populate: [
            {
              path: 'user',
              select: ['_id', 'info'],
            },
            {
              path: 'bugoBrand',
              select: ['_id', 'bugoAgency.nickName', 'bugoAgency.corpName'],
            },
          ],
        },
        {
          path: 'fevent',
          select: ['deceasedInfo.coffinOut'],
        },
        {
          path: 'funeralHomeInfo',
        },
      ],
      select: [
        'bugoDetail',
        'deliveryDetail.receiverAddress',
        'paymentDetail',
        'orderDetail',
        'bugoBrand',
        'shop',
      ],
      filter: {
        'paymentDetail.status': {
          $in: [PaymentState.CONFIRMED, PaymentState.PAID],
        },
        createdAt: { $gte: startofYear },
      },
      options: {
        sort: {
          'paymentDetail.purchasedAt': -1,
        },
      },
    })}`;
  }
};

//* 정산관련 페이지에 들어갈 shopOrder, 정산유무 상관없이 다들고옴 단 최대한 populate 없도록
export const shopOrderListForSellerByShopNeedAccountUrl = function (role: LowerRole) {
  return `${role}/shop-order/?${queryString({
    populate: [
      {
        path: 'bugo',
        select: ['user', 'bugoBrand', '_id'],
        populate: [
          {
            path: 'user',
            select: ['_id', 'info', 'bugoAgencyWorkerDetail'],
          },
          {
            path: 'bugoBrand',
            select: ['_id', 'bugoAgency.nickName', 'bugoAgency.corpName'],
          },
        ],
      },
      {
        path: 'funeralHomeInfo',
        select: ['_id', 'name', 'address'],
      },
      {
        path: 'fevent',
        select: ['deceasedInfo.coffinOut'],
      },
    ],
    select: [
      'bugoDetail',
      'deliveryDetail.receiverAddress',
      'paymentDetail',
      'orderDetail',
      'bugoBrand',
      'shop',
    ],
    filter: {
      'paymentDetail.status': {
        $in: [PaymentState.CONFIRMED, PaymentState.PAID],
      },
      'paymentDetail.isAccounted': { $ne: true },
    },
    options: {
      sort: {
        'paymentDetail.purchasedAt': -1,
      },
    },
  })}`;
};

export const useShopOrderListForSellerByShopAccountHook = function (
  shopId: string,
  selectYear?: number,
  enabled?: boolean,
) {
  const { sellerRole } = useAuth();

  useRecoilQuery(
    shopOrderListForSellerByShopAccountStatusAtomFamily(shopId),
    shopOrderListForSellerByShopAccountDataAtomFamily(shopId),
    shopOrderListForSellerByShopAccountUrl(shopId, sellerRole(), selectYear),
    fetcher,
    !enabled,
  );
};

export const useShopOrderListForSellerByShopNeedAccountHook = function (shopId: string) {
  const { sellerRole } = useAuth();

  useRecoilQuery(
    shopOrderListForSellerByShopNeedAccountStatusAtomFamily(shopId),
    shopOrderListForSellerByShopNeedAccountDataAtomFamily(shopId),
    shopOrderListForSellerByShopNeedAccountUrl(sellerRole()),
    fetcher,
  );
};

//* 상조팀장들이 본인이 접근가능한 shopOrder에 대한 hook
export const shopOrderListForWorkerUrl = function (role: LowerRole, selectYear?: number) {
  const now = new Date();
  const targetYear = selectYear ?? now.getFullYear();
  // 해당 연도 기준 ±7일 범위로 필터 (발인일 기준 조회도 고려)
  const startofYear = new Date(targetYear, 0, 1 - 7); // 1월 1일 - 7일
  const endofYear = new Date(targetYear, 11, 31 + 7); // 12월 31일 + 7일

  return `${role}/shop-order/?${queryString({
    populate: [
      {
        path: 'fevent',
      },
      {
        path: 'funeralHomeInfo',
      },
    ],
    filter: {
      'paymentDetail.status': {
        $in: [PaymentState.CONFIRMED, PaymentState.PAID],
      },
      // 정산 예정 내역(isRebated !== true)은 기간 상관없이, 정산 완료된 것은 연도별로 필터
      $or: [
        { 'paymentDetail.isRebated': { $ne: true } },
        {
          'paymentDetail.isRebated': true,
          createdAt: { $gte: startofYear, $lte: endofYear },
        },
      ],
    },
    options: {
      sort: {
        'paymentDetail.purchasedAt': -1,
      },
    },
  })}`;
};

export const useShopOrderListForWorkerHook = function (selectYear?: number) {
  const { bugoRole } = useAuth();

  useRecoilQuery(
    shopOrderListForWorkerStatusAtom,
    shopOrderListForWorkerDataAtom,
    shopOrderListForWorkerUrl(bugoRole(), selectYear),
    fetcher,
  );
};
